// src/pages/FolderView.js
import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import Header from "../../components/Header";
import NoteCard from "../../components/NoteCard"; 
import SwiftAlert from "../../components/SwiftAlert"; // ✅ Import SwiftAlert
import ShareModal from "../../components/User/ShareModal";
import { getFolderFiles, updateReviewer, getReviewerById } from "../../services/api"; 
import { useLibraryData } from "../../hooks/useLibraryData"; 
import { checkEnabled, normalizeColorKey } from "../../utils/libraryUtils";
import { generatePDFDocument } from "../../utils/pdfGenerator";
import { queryCache } from "../../lib/queryCache";

// --- Theme Mappings ---

const FOLDER_THEME_VARIANTS = {
  blue: { iconBg: "bg-blue-100", iconText: "text-blue-600" },
  orange: { iconBg: "bg-orange-100", iconText: "text-orange-600" },
  purple: { iconBg: "bg-purple-100", iconText: "text-purple-600" },
  emerald: { iconBg: "bg-emerald-100", iconText: "text-emerald-600" },
  red: { iconBg: "bg-red-100", iconText: "text-red-600" },
  gray: { iconBg: "bg-slate-100", iconText: "text-slate-600" },
};

const LIST_ITEM_VARIANTS = {
  blue:    { iconBg: "bg-blue-100",    iconText: "text-blue-600",    activeBorder: "border-blue-500",    activeBg: "bg-blue-50",    ring: "ring-blue-500" },
  orange:  { iconBg: "bg-orange-100",  iconText: "text-orange-600",  activeBorder: "border-orange-500",  activeBg: "bg-orange-50",  ring: "ring-orange-500" },
  purple:  { iconBg: "bg-purple-100",  iconText: "text-purple-600",  activeBorder: "border-purple-500",  activeBg: "bg-purple-50",  ring: "ring-purple-500" },
  emerald: { iconBg: "bg-emerald-100", iconText: "text-emerald-600", activeBorder: "border-emerald-500", activeBg: "bg-emerald-50", ring: "ring-emerald-500" },
  red:     { iconBg: "bg-red-100",     iconText: "text-red-600",     activeBorder: "border-red-500",     activeBg: "bg-red-50",     ring: "ring-red-500" },
  gray:    { iconBg: "bg-slate-100",   iconText: "text-slate-600",   activeBorder: "border-slate-500",   activeBg: "bg-slate-50",   ring: "ring-slate-500" },
};

const FolderView = () => {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();

  const { folders, markNoteAccessed } = useLibraryData();

  // -- Folder Metadata --
  const folderFromState = location.state?.folder;
  const [folderMeta, setFolderMeta] = useState(folderFromState || null);
  const folderId = params.folderId || params.id || folderFromState?._id || folderFromState?.id;

  // -- State (Cached for instant load) --
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [files, setFiles] = useState(() => {
    if (!folderId) return [];
    const cached = queryCache.get(`folder_files_${folderId}`);
    return Array.isArray(cached) ? cached : cached?.files ?? [];
  });
  const [loading, setLoading] = useState(() => {
    if (!folderId) return false;
    return !queryCache.get(`folder_files_${folderId}`);
  });

  // -- Move Modal & Alert State --
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
  const [noteToMove, setNoteToMove] = useState(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [noteToShare, setNoteToShare] = useState(null);
  const [alert, setAlert] = useState(null); // ✅ Added Alert State

  const folderName = folderMeta?.name || "Folder";
  
  const folderTheme = useMemo(() => {
    const key = normalizeColorKey(folderMeta?.color);
    return FOLDER_THEME_VARIANTS[key] || FOLDER_THEME_VARIANTS.blue;
  }, [folderMeta?.color]);

  const q = useMemo(() => {
    return (new URLSearchParams(location.search).get("q") || "").toLowerCase().trim();
  }, [location.search]);

  // -- Fetch Content --
  useEffect(() => {
    const fetchContent = async () => {
      if (!folderId) {
        setLoading(false);
        return;
      }

      // If already cached, render immediately without spinner
      const cached = queryCache.get(`folder_files_${folderId}`);
      if (cached) {
        const filesArr = Array.isArray(cached) ? cached : cached?.files ?? [];
        setFiles(filesArr);
        if (cached?.folder) {
          setFolderMeta(cached.folder);
        }
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await getFolderFiles(folderId);

        const filesArr = Array.isArray(data) ? data : data?.files ?? [];
        setFiles(filesArr);

        if (data?.folder) {
          setFolderMeta(data.folder);
        }
      } catch (err) {
        console.error("[FolderView] Failed to load content:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [folderId]);

  // -- Filtering --
  const filteredFiles = useMemo(() => {
    if (!q) return files;
    return files.filter((f) => {
      const title = (f.title || f.topic || "").toLowerCase();
      const meta = (f.meta || "").toLowerCase();
      return title.includes(q) || meta.includes(q);
    });
  }, [files, q]);

  const toggleSidebar = () => setIsSidebarOpen((s) => !s);

  // 1. Rename with Validation
  const handleRenameNote = async (note, newTitle) => {
    const cleanTitle = newTitle.trim();
    if (!cleanTitle) return;

    // Validation: Check duplicates within this folder
    const isDuplicate = files.some(f => 
        (f.title || f.topic || "").trim().toLowerCase() === cleanTitle.toLowerCase() &&
        f._id !== note._id 
    );

    if (isDuplicate) {
        // ✅ SwiftAlert Warning
        setAlert({
            type: "warning",
            title: "Name Taken",
            message: `A file named "${cleanTitle}" already exists in this folder.`
        });
        return;
    }

    try {
      setFiles((prev) => prev.map(f => 
        (f._id === note._id) ? { ...f, title: cleanTitle, topic: cleanTitle } : f
      ));
      await updateReviewer(note._id, { title: cleanTitle });
      setAlert({ type: 'success', title: 'Renamed', message: 'File renamed successfully.' });
    } catch (error) {
      console.error("Rename failed", error);
      setAlert({
        type: 'error',
        title: 'Failed',
        message: error?.response?.data?.error || error?.message || 'Failed to rename file.',
      });
    }
  };

  const handleColorChange = async (note, newColor) => {
    try {
      setFiles((prev) => prev.map(f => 
        (f._id === note._id) ? { ...f, color: newColor } : f
      ));
      await updateReviewer(note._id, { color: newColor });
    } catch (error) {
      console.error("Color update failed", error);
      setAlert({ type: 'error', title: 'Failed', message: 'Failed to update color.' });
    }
  };

  const openMoveModal = (note) => {
    setNoteToMove(note);
    setIsMoveModalOpen(true);
  };

  const openShareModal = (note) => {
    setNoteToShare(note);
    setIsShareModalOpen(true);
  };

  const handleShareModalUpdateNote = (updatedNote) => {
    setFiles((previous) =>
      previous.map((file) =>
        String(file._id || file.id) === String(updatedNote._id || updatedNote.id)
          ? updatedNote
          : file
      )
    );
  };

  const handleMoveSubmit = async (targetFolderId) => {
    if (!noteToMove) return;
    try {
      await updateReviewer(noteToMove._id, { folderId: targetFolderId });
      
      // If moved out of current folder, remove from view
      if (targetFolderId !== folderId) {
        setFiles((prev) => prev.filter(f => f._id !== noteToMove._id));
      }
      
      setIsMoveModalOpen(false);
      setNoteToMove(null);
      setAlert({ type: 'success', title: 'Moved', message: 'File moved successfully.' });
    } catch (error) {
      console.error("Move failed", error);
      setAlert({ type: 'error', title: 'Failed', message: 'Failed to move file.' });
    }
  };

  const handleStudy = (note, requestedMode) => {
    const fileId = note?._id || note?.id;
    if (fileId && typeof markNoteAccessed === "function") {
      markNoteAccessed(fileId);
    }

    const cfg = note.config || {};
    const hasSummary = checkEnabled(cfg.summaryEnabled);
    const hasFlashcards = checkEnabled(cfg.flashcardsEnabled);
    const hasQuiz = checkEnabled(cfg.quizEnabled);

    let mode = requestedMode;
    if (!mode) {
      if (hasSummary) mode = "summary";
      else if (hasFlashcards) mode = "flashcards";
      else if (hasQuiz) mode = "quiz";
    }

    if (!mode) return;

    navigate("/study-mode", {
      state: {
        mode,
        noteData: { ...note, folderName },
        reviewerId: note._id || note.id,
      },
    });
  };

  const handleDownloadPdf = async (note) => {
    const reviewerId = note?._id || note?.id;
    if (!reviewerId) {
      setAlert({ type: "error", title: "Failed", message: "Reviewer ID is missing." });
      return;
    }

    try {
      const raw = await getReviewerById(reviewerId);
      const normalized = raw && raw.data && raw.status ? raw.data : raw;
      const meta = normalized?.meta || normalized?.data?.meta || {};
      const data =
        normalized?.meta && normalized?.data
          ? normalized.data
          : normalized?.data?.data ?? normalized?.data ?? normalized;

      const summary =
        (typeof data === "string" ? data : "") ||
        data?.summaryHtml ||
        data?.summary ||
        data?.html ||
        data?.content ||
        "";

      const flashcardsRaw = data?.flashcards || data?.flashCards || data?.cards || [];
      const flashcards = Array.isArray(flashcardsRaw)
        ? flashcardsRaw
            .map((c) => ({
              front: c?.front || c?.term || c?.question || c?.q || c?.title || "",
              back: c?.back || c?.definition || c?.answer || c?.a || c?.content || "",
            }))
            .filter((c) => c.front || c.back)
        : [];

      const quizRaw = data?.quiz || data?.questions || [];
      const quiz = Array.isArray(quizRaw)
        ? quizRaw
            .map((q) => {
              const optsRaw = q?.options || q?.choices || q?.answers || [];
              const options = Array.isArray(optsRaw)
                ? optsRaw
                    .map((o) => (typeof o === "string" ? o : o?.text || o?.label || ""))
                    .filter(Boolean)
                : [];
              return {
                question: q?.question || q?.prompt || q?.q || "",
                options,
                answer: q?.answer || q?.correctAnswer || q?.correct || "",
                explanation: q?.explanation || q?.reason || "",
              };
            })
            .filter((q) => q.question)
        : [];

      if (!summary && flashcards.length === 0 && quiz.length === 0) {
        setAlert({ type: "warning", title: "No Content", message: "This reviewer has no downloadable content yet." });
        return;
      }

      const cfg = { ...(meta?.config || {}), ...(note?.config || {}) };
      generatePDFDocument({
        title: note?.title || note?.topic || meta?.topic || "Reviewer",
        generatedData: { summary, flashcards, quiz },
        config: {
          ...cfg,
          summaryEnabled: checkEnabled(cfg.summaryEnabled),
          flashcardsEnabled: checkEnabled(cfg.flashcardsEnabled),
          quizEnabled: checkEnabled(cfg.quizEnabled),
        },
      });
    } catch (error) {
      setAlert({
        type: "error",
        title: "Failed",
        message: error?.message || "Failed to download PDF.",
      });
    }
  };

  const handleUploadRedirect = () => {
    navigate("/create-reviewer", {
      state: {
        prefillFolderId: folderId || "",
        prefillFolderName: folderName || "",
        prefillInputMode: "file",
      },
    });
  };

  const handleArchiveNote = async (note) => {
    try {
      setFiles((prev) => prev.filter((f) => f._id !== note._id));
      await updateReviewer(note._id, { archived: true });
      setAlert({ type: 'success', title: 'Archived', message: `"${note.title || note.topic}" moved to archive.` });
    } catch (error) {
      setAlert({ type: 'error', title: 'Failed', message: 'Failed to archive note.' });
    }
  };

  return (
    <div className="bg-slate-50 text-slate-800 font-sans antialiased flex h-screen overflow-hidden text-left">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />

      <main className="flex-1 flex flex-col h-full overflow-hidden relative">
        <Header toggleSidebar={toggleSidebar} />

        <div className="flex-1 overflow-y-auto p-4 md:p-8">
          <div className="max-w-7xl mx-auto w-full animate-fade-in pb-20">
            
            {/* --- Page Header --- */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-100 pb-6 mb-8">
              <div>
                <nav className="flex items-center text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">
                  <span
                    className="cursor-pointer hover:text-blue-600 transition"
                    onClick={() => navigate("/library")}
                  >
                    Library
                  </span>
                  <i className="fa-solid fa-chevron-right mx-2 text-[8px]"></i>
                  <span className="text-slate-600">{folderName}</span>
                </nav>

                <div className="flex items-center gap-4 text-left">
                  <div className={`w-12 h-12 rounded-xl ${folderTheme.iconBg} ${folderTheme.iconText} flex items-center justify-center text-xl shadow-sm`}>
                    <i className="fa-solid fa-folder"></i>
                  </div>

                  <div>
                    <h1 className="text-2xl font-bold text-slate-900 leading-none mb-1">
                      {folderName}
                    </h1>
                    <p className="text-xs text-slate-500">
                      {loading
                        ? "Loading..."
                        : `${filteredFiles.length} ${q ? "matched files" : "Files"}`}
                    </p>
                  </div>
                </div>
              </div>

              <button
                onClick={handleUploadRedirect}
                className="bg-blue-600 text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-blue-700 shadow-lg shadow-blue-200 transition flex items-center justify-center gap-2"
              >
                <i className="fa-solid fa-cloud-arrow-up"></i> Upload File
              </button>
            </div>

            {/* --- Grid Content --- */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="h-48 bg-white rounded-xl border border-gray-100 animate-pulse shadow-sm" />
                ))}
              </div>
            ) : filteredFiles.length === 0 ? (
              <div className="bg-white border border-gray-100 rounded-2xl p-10 text-center shadow-sm">
                <div className="text-4xl text-slate-300 mb-3">
                  <i className={`fa-solid ${q ? "fa-magnifying-glass" : "fa-folder-open"}`}></i>
                </div>
                <h3 className="font-bold text-slate-800">
                  {q ? "No files found" : "No files yet"}
                </h3>
                <p className="text-sm text-slate-500 mt-1">
                  {q ? "Try a different keyword." : "Upload or generate a file to populate this folder."}
                </p>
                {!q && (
                  <button
                    onClick={handleUploadRedirect}
                    className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 transition"
                  >
                    <i className="fa-solid fa-cloud-arrow-up"></i>
                    Upload File
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredFiles.map((file) => (
                  <NoteCard
                    key={file._id || file.id}
                    note={file}
                    folderName={folderName}
                    showVerified={false}
                    onStudy={handleStudy}
                    onRename={handleRenameNote}
                    onColorChange={handleColorChange}
                    onMove={openMoveModal}
                    onDownloadPdf={handleDownloadPdf}
                    onShare={openShareModal}
                    onArchive={handleArchiveNote}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* --- Move Folder Modal --- */}
      {isMoveModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 animate-in zoom-in-95 duration-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Move to Folder</h3>
            <p className="text-sm text-gray-500 mb-4">Select a destination for <b>{noteToMove?.title || noteToMove?.topic}</b></p>
            
            <div className="max-h-60 overflow-y-auto space-y-2 mb-4 pr-1">
              {/* Option: Remove from folder */}
              <button
                onClick={() => handleMoveSubmit(null)}
                className="w-full text-left px-4 py-3 rounded-xl border border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition flex items-center gap-3"
              >
                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500">
                  <i className="fa-solid fa-folder-minus"></i>
                </div>
                <span className="font-semibold text-gray-700">No Folder</span>
              </button>

              {/* List existing folders with Dynamic Colors */}
              {folders.map(f => {
                const isActive = (noteToMove?.folder === f._id || noteToMove?.folder?._id === f._id || folderId === f._id);
                const theme = LIST_ITEM_VARIANTS[normalizeColorKey(f.color)] || LIST_ITEM_VARIANTS.blue;

                return (
                  <button
                    key={f._id}
                    onClick={() => handleMoveSubmit(f._id)}
                    className={`w-full text-left px-4 py-3 rounded-xl border transition flex items-center gap-3 ${
                      isActive 
                        ? `${theme.activeBorder} ${theme.activeBg} ring-1 ${theme.ring}` 
                        : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${theme.iconBg} ${theme.iconText}`}>
                      <i className="fa-solid fa-folder"></i>
                    </div>
                    
                    <span className="font-semibold text-gray-700 truncate">{f.name}</span>
                    
                    {isActive && (
                      <i className={`fa-solid fa-check ${theme.iconText} ml-auto`}></i>
                    )}
                  </button>
                );
              })}
            </div>

            <button 
              onClick={() => setIsMoveModalOpen(false)}
              className="w-full py-2.5 rounded-xl font-bold text-gray-500 hover:bg-gray-100 transition"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ✅ Render SwiftAlert */}
      {isShareModalOpen && noteToShare && (
        <ShareModal
          note={noteToShare}
          onClose={() => { setIsShareModalOpen(false); setNoteToShare(null); }}
          onUpdateNote={handleShareModalUpdateNote}
          onAlert={setAlert}
        />
      )}

      {alert && (
        <SwiftAlert 
          type={alert.type} 
          title={alert.title} 
          message={alert.message} 
          onClose={() => setAlert(null)} 
        />
      )}
    </div>
  );
};

export default FolderView;
